import { types } from "mobx-state-tree";
import fetcher from "../utils/fetcher";

export const GLTypes = Object.freeze({
  A: "A",
  B: "B",
  C: "C",
});

const ProductsStore = types
  .model("ProductsStore", {
    searchedTerm: types.frozen(),
    searchInputValue: types.optional(types.string, ""),
    familyTypes: types.frozen(),
    familyLog: types.frozen(),
    orderConfirmed: types.frozen(),
    familyLogList: types.frozen(),
    categories: types.frozen(),
    colors: types.frozen(),
    cart: types.frozen(),
    projectCartProducts: types.frozen(),
    projectCart: types.frozen(),
    error: types.frozen(),
    selectedProduct: types.frozen(),
    filteredProducts: types.frozen(),
    isSearchByPart: types.optional(types.boolean, false),
  })
  .actions((self) => {
    const getFamilyTypes = async () => {
      self.setError("");
      const response = await fetcher().get.from.products.get_family_types();
      if (response?.status === 200 && response?.data) {
        self.setFamilyTypes(response.data);
        return response.data;
      } else {
        self.setError(
          response?.status === 400 || response?.status === 500
            ? response?.data?.FORM?.InterfaceErrors?.text || "error:error_failed_get_family_types"
            : "error:error_failed_get_family_types"
        );
      }
    };

    const getColors = async () => {
      self.setError("");
      const response = await fetcher().get.from.products.get_colors();
      if (response?.status === 200 && response?.data) {
        self.setColors(response.data.colorsRes);
        return response.data.colorsRes;
      } else {
        self.setError(
          response?.status === 400 || response?.status === 500
            ? response?.data?.FORM?.InterfaceErrors?.text
            : "error:error_failed_get_colors"
        );
      }
    };

    const getFamilyLog = async (data) => {
      setError("");
      const response = await fetcher().post(data).from.products.get_family_log();

      if (response?.status === 200 && response?.data) {
        self.setFamilyLog(response.data);

        return response.data;
      } else {
        self.setError(
          response?.status === 400 || response?.status === 500
            ? response?.data?.FORM?.InterfaceErrors?.text || "error:error_failed_get_family_log"
            : "error:error_failed_get_family_log"
        );
        return null;
      }
    };

    const getFamilyLogPart = async (data) => {
      setError("");
      const response = await fetcher().post(data).from.products.get_family_log_part();

      if (response?.status === 200 && response?.data) {
        return response.data;
      } else {
        self.setError(
          response?.status === 400 || response?.status === 500
            ? response?.data?.FORM?.InterfaceErrors?.text || "error:error_failed_get_family_log"
            : "error:error_failed_get_family_log"
        );
      }
    };

    const calcFrameOpenings = async (data) => {
      self.setError("");
      const response = await fetcher().post(data).from.products.calc_frame_openings();
      if (
        response?.status === 200 &&
        response?.data?.cavity != null &&
        response?.data?.exterior != null
      ) {
        return response.data;
      }
      self.setError(
        response?.status === 400 || response?.status === 500
          ? response?.data?.message || "error:error_failed_get_family_log"
          : "error:error_failed_get_family_log"
      );
      return null;
    };

    const setOrderConfirmed = (confirmed) => {
      self.orderConfirmed = confirmed;
    };

    const setSearchedTerm = (term) => {
      self.searchedTerm = term;
    };

    const setSearchInputValue = (value) => {
      self.searchInputValue = value ?? "";
    };

    // Single place to reset the header search bar state. Called from any
    // navigation that should visually "leave" the search context — logo,
    // nav links, breadcrumbs, etc.
    const clearSearch = () => {
      self.searchInputValue = "";
      self.searchedTerm = "";
      self.filteredProducts = [];
    };

    const setIsSearchByPart = (isSearchByPart) => {
      self.isSearchByPart = isSearchByPart;
    };

    const setFilteredProducts = (filterd) => {
      self.filteredProducts = filterd;
    };

    const setOrder = async (data) => {
      setError("");
      const response = await fetcher().post(data).from.common.general_load();
      if (response?.status === 200 && response?.data) {
        return response?.data;
      } else {
        self.setError(
          response?.status === 400 || response?.status === 500
            ? response?.data?.FORM?.InterfaceErrors?.text || "error:error_failed_set_order"
            : "error:error_failed_set_order"
        );
      }
    };

    const getCart = async (data) => {
      setError("");
      const response = await fetcher().post(data).from.common.get_general_load();
      if (response?.status === 200 && response?.data) {
        const cartData = response.data;
        self.setCart(cartData, data.TYPE);

        return cartData;
      } else {
        self.setError(
          response?.status === 400 || response?.status === 500
            ? response?.data?.FORM?.InterfaceErrors?.text || "error:error_failed_get_order"
            : "error:error_failed_get_order"
        );
      }
    };

    const addCartLine = async (data) => {
      setError("");
      const response = await fetcher().post(data).from.common.add_general_load_line();
      if (response?.status === 200 && response?.data) {
      } else {
        self.setError(
          response?.status === 400 || response?.status === 500
            ? response?.data?.FORM?.InterfaceErrors?.text || "error:error_failed_update_order"
            : "error:error_failed_update_order"
        );
      }
    };

    const updateCartLine = async (data) => {
      setError("");
      const response = await fetcher().post(data).from.common.update_general_load_line();
      if (response?.status === 200 && response?.data) {
        await self.getCart(data);
      } else {
        self.setError(
          response?.status === 400 || response?.status === 500
            ? response?.data?.FORM?.InterfaceErrors?.text || "error:error_failed_update_order"
            : "error:error_failed_update_order"
        );
      }
    };

    const deleteCartLine = async (data) => {
      setError("");
      const response = await fetcher().post(data?.data).from.common.delete_general_load_line();
      if (response?.status === 200) {
        await self.getCart(data);
      } else {
        self.setError(
          response?.status === 400 || response?.status === 500
            ? response?.data?.FORM?.InterfaceErrors?.text || "error:error_failed_delete_order"
            : "error:error_failed_delete_order"
        );
      }
    };

    const updateOrderTransmit = async (data) => {
      setError("");
      const response = await fetcher().post(data).from.common.update_general_load();
      if (response?.status === 200) {
        await self.getCart(data);
        return true;
      } else {
        self.setError(
          response?.status === 400 || response?.status === 500
            ? response?.data?.FORM?.InterfaceErrors?.text ||
                "error:error_failed_update_order_transmit"
            : "error:error_failed_update_order_transmit"
        );
        return false;
      }
    };

    const setError = (error) => {
      self.error = error;
    };

    const setFamilyTypes = (familyTypes) => {
      self.familyTypes = familyTypes;
    };

    const setFamilyLog = (familyLog) => {
      self.familyLog = familyLog;
    };

    const setFamilyLogList = (familyLogList) => {
      self.familyLogList = familyLogList;
    };

    const setCategories = (categories) => {
      self.categories = categories;
    };

    const setColors = (colors) => {
      self.colors = colors;
    };

    const setSelectedProduct = (selectedProduct) => {
      self.selectedProduct = selectedProduct;
    };

    const getActiveCategory = () => {
      return self.activeCategory;
    };

    const getActiveItem = () => {
      return self.activeItem;
    };
    const setActiveCategory = (cat) => {
      self.activeCategory = cat;
    };

    const setActiveItem = (item) => {
      self.activeItem = item;
    };

    const setCart = async (cart, type) => {
      let chartList = {};
      const flatCartRes = cart?.flat(1);

      if (flatCartRes?.length > 0) {
        const mapList = flatCartRes.map((p) =>
          p?.ZCOR_GENERALLOADLOGL_SUBFORM?.map((line) => {
            return {
              KLINE: p.KLINE,
              project: p.TEXT14,
              TYPE: p.TYPE,
              LINE: line.LINE,
              thumbnail: line.ZACP_EXTFILENAME,
              file: line.EXTFILENAME,
              filename: line.TEXT9,
              price: line.PRICE,
              id: line.TEXT2,
              description: line.TEXT5,
              openingType: line.TEXT3,
              length: line.REAL1,
              width: line.REAL2,
              quantity: line.INT4,
              color: line.TEXT4,
              labelInstructions: line.TEXT10,
              stickerInstructions: line.TEXT10,
              productionInstraction: line.TEXT7,
              buildingNo: line.INT1,
              floorNo: line.INT2,
              apartmentNo: line.INT3,
              ORDI: line.INT6,
              ORD: p.INT5,
              balance: line.INT8,
            };
          })
        );

        const mapListFlat = mapList.flat(1);

        if (type === GLTypes.B) {
          chartList = mapListFlat?.reduce((acc, currentValue) => {
            let groupKey = currentValue["id"];
            if (!acc[groupKey]) {
              acc[groupKey] = [];
            }
            acc[groupKey].push(currentValue);
            return acc;
          }, {});

          self.cart = chartList;
        }

        if (type === GLTypes.C) {
          chartList = mapListFlat.reduce((projectAcc, item) => {
            const { project, id } = item;

            if (!projectAcc[project]) {
              projectAcc[project] = {};
            }

            if (!projectAcc[project][id]) {
              projectAcc[project][id] = [];
            }
            projectAcc[project][id].push(item);

            return projectAcc;
          }, {});
          self.projectCartProducts = chartList;
        }
      } else {
        if (type === GLTypes.B) {
          self.cart = chartList;
        }
        if (type === GLTypes.C) {
          self.projectCartProducts = chartList;
        }
      }
    };

    return {
      setError,
      setOrder,
      getFamilyTypes,
      setFamilyTypes,
      getColors,
      getFamilyLog,
      setFamilyLog,
      setColors,
      getCart,
      setCart,
      addCartLine,
      updateCartLine,
      deleteCartLine,
      updateOrderTransmit,
      getFamilyLogPart,
      calcFrameOpenings,
      setFamilyLogList,
      setCategories,
      setSelectedProduct,
      setActiveCategory,
      setActiveItem,
      getActiveItem,
      getActiveCategory,
      setSearchedTerm,
      setSearchInputValue,
      clearSearch,
      setFilteredProducts,
      setIsSearchByPart,
      setOrderConfirmed,
    };
  })
  .views((self) => ({
    get isError() {
      return !!self.error;
    },
  }));

const productsStore = ProductsStore.create({
  searchedTerm: "",
  filteredProducts: [],
  error: "",
  cart: {},
  projectCart: [],
  projectCartProducts: {},
  familyTypes: [],
  familyLog: [],
  familyLogList: [],
  categories: [],
  selectedProduct: null,
  orderConfirmed: null,
  isSearchedByPart: false,
});

export default productsStore;
